import React, { ReactElement } from 'react';
import CheckAccess from '../../../../components/check-access/check-access.component';
import PaddedFlexContainer from '../../../../components/padded-flex-container/padded-flex-container.component';
import FlexContainer from '../../../../components/flex-container/flex-container.component';
import FilteredText from '../../../../components/filtered-text/filtered-text.component';
import TextBold from '../../../../components/text-bold/text-bold.component';
import Tooltip from '../../../../components/tooltip/tooltip.component';
import TextSmallSize from '../../../../components/text-small-size/text-small-size.component';
import FileLink from '../../../../components/file-link/file-link.component';
import TextBlock from '../../../../components/text-block/text-block.component';
import AttachmentIcon from '../../../../icons/attachment-icon';
import CommentIcon from '../../../../icons/comment-icon';
import { CONTACT_VIEW, SEARCH_RESULT_VIEW } from '../../../../constants/entity/permissions';
import LINK_TYPE_FILE_LINK from '../../../../constants/entity/link-types';
import COMMENT_TYPE_SYSTEM from '../../../../constants/entity/comment-types';

const LastUpdateBlock: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isDisabled,
    withWrap,
    withNoUpdates,
    withNoTooltips,
    withSmallText,
    userText,
    dateText,
    commentListViewPermissions,
    attachmentListViewPermissions,
    className,
    commentList,
    attachmentList,
  } = props;

  const lastUpdateText: string | null = userText && dateText ? `By ${userText} ${dateText}` : null;
  const userComments: Comment[] = commentList
    ? commentList.filter((commentItem: Comment): boolean => commentItem.type !== COMMENT_TYPE_SYSTEM) : [];
  const lastComment: Comment | null = userComments.length ? userComments[userComments.length - 1] : null;
  const lastCommentFileLinks: CommentLink[] = lastComment && lastComment.links && lastComment.links.length
    ? lastComment.links.filter((commentLink: CommentLink): boolean => (
      commentLink.type === LINK_TYPE_FILE_LINK
    )) : [];
  const commentsTotal: number = userComments.length;

  let withAttachments: boolean = !!attachmentList && !!attachmentList.length;
  if (!withAttachments && userComments.length) {
    userComments.forEach((commentItem: Comment): void => {
      if (commentItem.attachments && !!commentItem.attachments.length) {
        withAttachments = true;
      }
    });
  }

  return (
    <FlexContainer isSpread isColumn={withWrap} className={className}>
      {
        !withNoUpdates && lastUpdateText && (
          <CheckAccess permissions={commentListViewPermissions}>
            <PaddedFlexContainer isDefault isInline={withSmallText} minWidth={80}>
              <TextBlock withWordBreak>
                {
                  withSmallText ? (
                    <TextSmallSize isInline>
                      {lastUpdateText}
                    </TextSmallSize>
                  ) : lastUpdateText
                }
              </TextBlock>
            </PaddedFlexContainer>
          </CheckAccess>
        )
      }
      <PaddedFlexContainer isInline paddingLeft={5} minWidth={100}>
        {
          !!commentsTotal && (
            <CheckAccess permissions={commentListViewPermissions}>
              <Tooltip
                withLeftPlacement
                isDisabled={withNoTooltips}
                isInvisible={!lastComment}
                title={(
                  <span>
                    <FilteredText
                      withMentions
                      withHashtags
                      text={lastComment ? lastComment.text : ''}
                      mentionPermission={CONTACT_VIEW}
                      hashtagPermission={SEARCH_RESULT_VIEW}
                    />
                    {
                      lastCommentFileLinks && !!lastCommentFileLinks.length
                      && lastCommentFileLinks.map((commentLinkItem: CommentLink): ReactElement => (
                        <FileLink
                          isDisabled={isDisabled}
                          key={commentLinkItem.id}
                          fileLink={commentLinkItem}
                        />
                      ))
                    }
                  </span>
                )}
              >
                <FlexContainer>
                  <FlexContainer>
                    <CommentIcon />
                  </FlexContainer>
                  {
                    !!commentsTotal && (
                      <PaddedFlexContainer isSmall>
                        <TextBold>
                          {commentsTotal}
                        </TextBold>
                      </PaddedFlexContainer>
                    )
                  }
                </FlexContainer>
              </Tooltip>
            </CheckAccess>
          )
        }
        {
          withAttachments && (
            <CheckAccess permissions={attachmentListViewPermissions}>
              {
                !withNoTooltips ? (
                  <Tooltip withLeftPlacement title="Has Attachments">
                    <PaddedFlexContainer>
                      <AttachmentIcon />
                    </PaddedFlexContainer>
                  </Tooltip>
                ) : (
                  <PaddedFlexContainer>
                    <AttachmentIcon />
                  </PaddedFlexContainer>
                )
              }
            </CheckAccess>
          )
        }
      </PaddedFlexContainer>
    </FlexContainer>
  );
});

export default LastUpdateBlock;
